import { z } from 'zod';
import {
  ActivityDataEntityType,
  DateTimeSchema,
  objectIdRef,
  ObjectIdSchema,
  ObjectIdString,
  OpportunityTagDTO,
  OpportunityType,
  RealOrDemoOpportunityType,
} from '../common.js';
import {
  entityTagTypes,
  nonEntityTagTypes,
  OpportunityScoringDetails,
  OpportunityStatus,
} from '../../opportunityTypes.js';
import { LlmPromptDTO } from './llmPromptSchema.js';
import { Interval } from 'luxon';

// Zod schema for Luxon Interval
const IntervalSchema = z.custom<Interval>((val) => val instanceof Interval, {
  message: 'Expected Luxon Interval',
});

const DescriptionEntry = z.object({
  description: z.string(),
  shortDescription: z.string(),
  summary: z.string().optional(),
});

export const OpportunityDescriptions = z.object({
  manager: DescriptionEntry,
  recruiter: DescriptionEntry,
});
export type OpportunityDescriptions = z.infer<typeof OpportunityDescriptions>;

export const VisibilityMetadata = z.object({
  enabled: z.boolean(),
  date: DateTimeSchema,
});
export type VisibilityMetadata = z.infer<typeof VisibilityMetadata>;

export const ValidationResponseDTO = z.object({
  _id: ObjectIdString,
  parsedResponse: z
    .object({
      description: z.string().optional(),
      shortDescription: z.string().optional(),
      justification: z.string().optional(),
      descriptions: z
        .object({
          manager: z.object({
            description: z.string(),
            shortDescription: z.string(),
            summary: z.string().optional(),
          }),
          recruiter: z.object({
            description: z.string(),
            shortDescription: z.string(),
            summary: z.string().optional(),
          }),
        })
        .optional(),
    })
    .passthrough()
    .nullish(),
  promptType: z.string().optional(),
  prompt: LlmPromptDTO.or(ObjectIdString).optional(),
  responseDate: DateTimeSchema.optional(),
});
export type ValidationResponseDTO = z.infer<typeof ValidationResponseDTO>;

export const OpportunityAutoResolveResult = z.looseObject({
  resolved: z.boolean(),
  status: OpportunityStatus.optional(),
  reason: z.string().optional(),
  timestamp: DateTimeSchema.optional(),
  replacedBy: ObjectIdSchema.optional(),
});
export type OpportunityAutoResolveResult = z.infer<typeof OpportunityAutoResolveResult>;

export const OpportunityAutoResolveResultDTO = OpportunityAutoResolveResult.extend({
  replacedBy: ObjectIdString.optional(),
});

// Response Time Service Schemas
// These are service-level schemas used in responseTimeService.ts

// A trimmed-down version of a CandidateActivity
export const OpportunityMessage = z.object({
  _id: ObjectIdString,
  timestamp: DateTimeSchema,
  candidate: ObjectIdString.nullish(),
  candidateSourceId: z.string().nullish(),
  candidateName: z.string().nullish(),
  type: z.string().nullish(),
  category: z.string().nullish(),
  content: z.string().nullish(),
  isInbound: z.boolean().nullish(),
  isOutbound: z.boolean().nullish(),
  recruiterName: z.string().nullish(),
});
export type OpportunityMessage = z.infer<typeof OpportunityMessage>;

export const MessagePair = z.object({
  inboundMessage: OpportunityMessage,
  responseMessage: OpportunityMessage,
  responseTimeMinutes: z.number(),
});
export type MessagePair = z.infer<typeof MessagePair>;

export const ResponseTimeMetrics = z.object({
  recruiterName: z.string(),
  historicalAverage: z.number(),
  currentAverage: z.number(),
  percentageChange: z.number(),
  historicalMessageCount: z.number(),
  currentMessageCount: z.number(),
  currentPeriodExamples: MessagePair.array(),
  historicalPeriodExamples: MessagePair.array(),
  historicalPeriod: IntervalSchema.or(z.record(z.string(), z.unknown())),
  currentPeriod: IntervalSchema.or(z.record(z.string(), z.unknown())),
  weeklyData: z.array(
    z.object({
      startDate: DateTimeSchema,
      endDate: DateTimeSchema,
      avgResponseTime: z.number(),
      messageCount: z.number(),
    })
  ),
});
export type ResponseTimeMetrics = z.infer<typeof ResponseTimeMetrics>;

const UserResolveResult = z.object({
  user: z.object({
    id: ObjectIdString,
    name: z.string(),
    email: z.string().optional(),
  }),
  previousStatus: z.string().optional(),
  status: z.string(),
});

export const OpportunityData = z.object({
  activityMetadata: z
    .object({
      emails: z.number(),
      textMessages: z.number(),
      calls: z.number(),
      totalOutbound: z.number(),
      totalInbound: z.number(),
      mostRecentSubmissionDate: DateTimeSchema.optional(),
      mostRecentSubmissionBillRate: z.number().optional(),
      mostRecentPlacementDate: DateTimeSchema.optional(),
      mostRecentPlacementBillRate: z.number().optional(),
    })
    .optional(),

  lastInboundActivity: DateTimeSchema.optional(),
  positionStartDate: DateTimeSchema.optional(),
  positionEndDate: DateTimeSchema.optional(),
  surveySentDate: DateTimeSchema.optional(),
  candidateCreatedDate: DateTimeSchema.optional(),
  completedDate: DateTimeSchema.nullish(),
  timestamp: DateTimeSchema.optional(),

  targetEngagementRate: z.number().optional(),
  numCancellationsInPastThirtyDays: z.number().optional(),
  currentTrendLength: z.number().optional(),

  questions: z.array(z.string()).optional(),
  fullNoteText: z.string().optional(),
  accountName: z.string().optional(),

  // HandoffFailure / InboundFollowUp opportunity type fields
  flowId: ObjectIdString.optional(),
  flowName: z.string().optional(),
  startNoteId: ObjectIdString.optional(),
  startAction: z.string().optional(),
  completionWindowDays: z.number().int().positive().optional(),
  expectedCompletionDate: DateTimeSchema.optional(),
  inboundNoteId: ObjectIdString.optional(),

  autoResolveResult: OpportunityAutoResolveResultDTO.optional(),

  ownerResolveResult: UserResolveResult.nullish(),
  // TODO: change name to "managerResolveResult" and run an update script?
  userResolveResult: UserResolveResult.nullish(),

  templateInput: z.record(z.string(), z.unknown()).optional(),

  // Response time metrics (only for responseTimeImprovement/responseTimeDecline opportunities)
  responseTimeMetrics: ResponseTimeMetrics.optional(),

  // LossToCompetitor opportunity type fields
  competitorName: z.string().nullish(),
});
export type OpportunityData = z.infer<typeof OpportunityData>;

export const PopulatedAccountDTO = z.object({
  _id: ObjectIdString,
  name: z.string(),
  status: z.string(),
  integrations: z
    .object({
      type: z.string(),
      corpId: z.string().optional(),
      apiUserId: z.string().optional(),
    })
    .array()
    .optional(),
  corpId: z.string().optional(),
  apiUserId: z.string().optional(),
});
export type PopulatedAccountDTO = z.infer<typeof PopulatedAccountDTO>;

const PositionRawData = z.object({
  clientCorporation: z
    .object({
      id: z.number(),
      name: z.string(),
    })
    .optional(),
  title: z.string().optional(),
});
export type PositionRawData = z.infer<typeof PositionRawData>;

const Position = z.object({
  _id: ObjectIdString,
  sourceId: z.string().optional(),
  rawData: PositionRawData.optional(),
});

const PlacementMappedData = z.object({
  _id: z.string(),
  jobTitle: z.string(),
  startDate: DateTimeSchema,
});
export type PlacementMappedData = z.infer<typeof PlacementMappedData>;

export const PlacementRawData = z.object({
  status: z.string().nullish(),
  // TODO: change these?
  dateBegin: z.number().nullish(),
  dateEnd: z.number().nullish(),
  jobOrder: z
    .object({
      id: z.number().nullish(),
      title: z.string().nullish(),
      clientCorporation: z
        .object({
          id: z.number().nullish(),
          name: z.string().nullish(),
        })
        .nullish(),
    })
    .nullish(),
});
export type PlacementRawData = z.infer<typeof PlacementRawData>;

const EntityReference = z.object({
  _id: ObjectIdString,
  sourceId: z.string().nullish(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  department: z.string().nullish(),
  name: z.string().nullish(),
  positionStartDate: z.string().or(DateTimeSchema).nullish(),
  mappedData: z.record(z.string(), z.unknown()).optional(),
  rawData: z.record(z.string(), z.unknown()).optional(),
});
export type EntityReference = z.infer<typeof EntityReference>;

const PlacementReference = EntityReference.extend({
  mappedData: PlacementMappedData.nullish(),
  rawData: PlacementRawData.nullish(),
});
export type PlacementReference = z.infer<typeof PlacementReference>;

export const EntityTagType = z.enum(entityTagTypes);
export const NonEntityTagType = z.enum(nonEntityTagTypes);

export const IOpportunityEntityTag = z.object({
  tagType: EntityTagType,
  entity: ObjectIdSchema,
  entitySourceId: z.string(),
  name: z.string().optional(),
  department: z.string().optional(),
  type: z.string().optional(),
});
export type IOpportunityEntityTag = z.infer<typeof IOpportunityEntityTag>;

export const IOpportunityNonEntityTag = z.object({
  tagType: NonEntityTagType,
  entity: ObjectIdSchema.optional(),
  entitySourceId: z.string().optional(),
  name: z.string().optional(),
});
export type IOpportunityNonEntityTag = z.infer<typeof IOpportunityNonEntityTag>;

export const IOpportunityTag = z.discriminatedUnion('tagType', [
  IOpportunityEntityTag,
  IOpportunityNonEntityTag,
]);
export type IOpportunityTag = z.infer<typeof IOpportunityTag>;

export const IOpportunity = z.object({
  _id: ObjectIdSchema,
  account: objectIdRef('Account'),
  candidate: objectIdRef('Candidate').optional(),
  recruiter: objectIdRef('Recruiter').optional(),
  manager: objectIdRef('Recruiter').nullish(),
  position: objectIdRef('Position').optional(),
  placement: objectIdRef('Placement').optional(),
  submission: objectIdRef('Submission').optional(),
  notes: objectIdRef('Note').array().optional(),
  subActivities: z.string().array().optional(),
  company: objectIdRef('AtsCompany').optional(),
  type: OpportunityType,
  status: OpportunityStatus,
  isRead: z.boolean().optional(),
  isReadByRecruiter: z.boolean().optional(),
  dismissUntil: DateTimeSchema.nullish(),
  validationResponse: objectIdRef('LlmResponse').optional(),
  revalidationResponses: objectIdRef('LlmResponse').array(),
  lastValidationResponse: objectIdRef('LlmResponse').optional(),
  score: z.number().optional(),
  tags: IOpportunityTag.array(),
  title: z.string().optional(),
  descriptions: OpportunityDescriptions.optional(),
  data: OpportunityData.optional(),
  createdAt: DateTimeSchema,
  updatedAt: DateTimeSchema,
  replacedBy: objectIdRef('Opportunity').optional(),

  metadata: z.object({
    lastRevalidated: DateTimeSchema.optional(),
    lastScored: DateTimeSchema.optional(),
    lastAttemptedAutoResolve: DateTimeSchema.optional(),
    scoringDetails: OpportunityScoringDetails.optional(),
    // TODO: add this back once visibility metadata implemented in generation
    // visibility: z.object({
    //   owner: VisibilityMetadata,
    //   manager: VisibilityMetadata,
    // }),
  }),
});
export type IOpportunity = z.infer<typeof IOpportunity>;

export const BaseOpportunityDTO = z.object({
  ...IOpportunity.shape,
  _id: ObjectIdString,
  type: RealOrDemoOpportunityType,
  account: PopulatedAccountDTO.pick({ _id: true, name: true }).or(ObjectIdString),
  tags: OpportunityTagDTO.array().optional(),
  validationResponse: ValidationResponseDTO.nullish(),
  revalidationResponses: ValidationResponseDTO.array().nullish(),
  lastValidationResponse: ValidationResponseDTO.nullish(),
  metadata: z
    .looseObject({
      scoringDetails: OpportunityScoringDetails.optional(),
      lastScored: DateTimeSchema.optional(),
    })
    .optional(),
});

// TODO: make this match the populatedOpportunityPaths in api/queryUtils
export const OpportunityDetailDTO = z.object({
  ...BaseOpportunityDTO.shape,
  candidate: z
    .object({
      _id: ObjectIdString,
      sourceId: z.string().optional(),
      name: z.string().optional(),
      firstName: z.string().optional(),
      lastName: z.string().optional(),
      rawData: z.record(z.string(), z.unknown()).optional(),
    })
    .optional(),

  recruiter: EntityReference.optional(),
  salesRep: EntityReference.optional(),
  position: Position.optional(),
  company: EntityReference.optional(),
  placement: PlacementReference.optional(),

  notes: ObjectIdString.array().optional(),
});
export type OpportunityDetailDTO = z.infer<typeof OpportunityDetailDTO>;

export const DashboardListOpportunityDTO = z.object({
  ...BaseOpportunityDTO.shape,
  candidate: z.record(z.string(), z.unknown()).nullish(),
  recruiter: z.record(z.string(), z.unknown()).nullish(),
  position: z.record(z.string(), z.unknown()).nullish(),
  company: ObjectIdString.nullish(),
  placement: z.record(z.string(), z.unknown()).nullish(),
});
export type DashboardListOpportunityDTO = z.infer<typeof DashboardListOpportunityDTO>;

export const ExtensionListOpportunityDTO = z.object({
  ...DashboardListOpportunityDTO.shape,
  account: z.record(z.string(), z.unknown()).nullish(),
});
export type ExtensionListOpportunityDTO = z.infer<typeof ExtensionListOpportunityDTO>;

export const OpportunityListExtensionQuery = z.object({
  statusForRecruiter: OpportunityStatus,
  unreadByRecruiter: z.boolean().optional(),
  hideDismissed: z.boolean().optional(),
});
export type OpportunityListExtensionQuery = z.infer<typeof OpportunityListExtensionQuery>;

export const OpportunityListExtensionInput = z.object({
  ...OpportunityListExtensionQuery.shape,
  cursor: ObjectIdString.nullish(),
  count: z.int().min(1).max(100).default(20),
});
export type OpportunityListExtensionInput = z.infer<typeof OpportunityListExtensionInput>;

export const OpportunityListExtensionResponse = z.object({
  opportunities: ExtensionListOpportunityDTO.array(),
  nextCursor: ObjectIdString.nullish(),
});
export type OpportunityListExtensionResponse = z.infer<typeof OpportunityListExtensionResponse>;

export const AskToroOpportunityInfo = z.object({
  opportunityId: ObjectIdString,
  entityType: ActivityDataEntityType,
  entityId: z.string(),
  entityName: z.string().optional(),
});
export type AskToroOpportunityInfo = z.infer<typeof AskToroOpportunityInfo>;
